import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, Globe, User, LogOut, Shield, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { goHome, goToAdmin, goToCommunity, goToCommunityAdmin, goToVendorAdmin, goToSignup, goToLogin, goToAbout, goToFAQ, goToGallery, goToCalendar, goToContact, goToVoteSection } from '../utils/navigation';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const { user, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false);
    } else {
      // If element not found, try again after a short delay
      setTimeout(() => {
        const elementRetry = document.getElementById(sectionId);
        if (elementRetry) {
          elementRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      setIsMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const toggleUserDropdown = () => {
    if (!isUserDropdownOpen && buttonRef.current) {
      // Calculate position when opening dropdown
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.right - 192, // 192px is w-48 (width of dropdown)
      });
    }
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && 
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside the speed dial menu and not on the menu button
      if (isMenuOpen && 
          !target.closest('[data-mobile-menu]') && 
          !target.closest('[data-mobile-menu-button]')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Update position on scroll and resize
  useEffect(() => {
    const updatePosition = () => {
      if (isUserDropdownOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.right - 192,
        });
      }
    };

    if (isUserDropdownOpen) {
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isUserDropdownOpen]);

  // Function to get dashboard navigation based on user role
  const getDashboardNavigation = () => {
    if (user?.role === 'ADMIN') {
      return goToAdmin;
    } else if (user?.role === 'COMMITTEE_ADMIN') {
      return goToCommunityAdmin;
    } else if (user?.role === 'VENDOR_ADMIN') {
      return goToVendorAdmin;
    } else if (['COMMITTEE', 'VENDOR'].includes(user?.role || '')) {
      return goToCommunity;
    } else {
      return goHome;
    }
  };

  // Function to get dashboard button text based on user role
  const getDashboardButtonText = () => {
    if (user?.role === 'ADMIN') {
      return 'Admin Dashboard';
    } else if (user?.role === 'COMMITTEE_ADMIN') {
      return 'Committee Admin Dashboard';
    } else if (user?.role === 'VENDOR_ADMIN') {
      return 'Vendor Admin Dashboard';
    } else if (['COMMITTEE', 'VENDOR'].includes(user?.role || '')) {
      return 'Committee Dashboard';
    } else {
      return 'Dashboard';
    }
  };

  return (
    <>
      {/* Promotional Bar */}
      <div className="bg-dpn-olive text-white py-2 overflow-hidden relative">
        <div className="flex items-center justify-center whitespace-nowrap animate-marquee">
          {Array.from({ length: 100 }).map((_, i) => (
            <React.Fragment key={i}>
              <span className="mx-4">Welcome to Durga Puja Network- an initiative of Bong Haat. This site is absolutely&nbsp;FREE&nbsp;to&nbsp;use</span>
              <span className="mx-2">
                {["✨", "🌸", "⭐", "🌺"][i % 4]}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <header className="bg-white/95 backdrop-blur-md z-50 shadow-sm relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Layout */}
        <div className="md:hidden flex items-center py-6 relative">
          {/* Mobile Menu Button - Top Right */}
          <button
            onClick={toggleMenu}
            data-mobile-menu-button
            className="absolute right-0 text-dpn-olive hover:text-dpn-pink transition-colors duration-200 p-2 z-10"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          
          {/* Logo - Centered and Large on Mobile */}
          <div className="flex items-center justify-center w-full">
            <div className="relative group">
              <img 
                src="/logo.png" 
                alt="Durga Puja Network" 
                title="Durga Puja Network"
                className="h-32 w-32 object-contain cursor-pointer hover:opacity-90 transition-opacity duration-200 drop-shadow-lg"
                onClick={goHome}
                onError={(e) => {
                  // Fallback to Globe icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <span title="Durga Puja Network">
                <Globe className="h-32 w-32 text-dpn-pink hidden cursor-pointer hover:opacity-90 transition-opacity duration-200 drop-shadow-lg" onClick={goHome} />
              </span>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <div className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="/logo.png" 
                alt="Durga Puja Network" 
                title="Durga Puja Network"
                className="h-24 w-24 lg:h-28 lg:w-28 object-contain cursor-pointer hover:opacity-90 transition-opacity duration-200 drop-shadow-md"
                onClick={goHome}
                onError={(e) => {
                  // Fallback to Globe icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <span title="Durga Puja Network">
                <Globe className="h-24 w-24 lg:h-28 lg:w-28 text-dpn-pink hidden cursor-pointer hover:opacity-90 transition-opacity duration-200 drop-shadow-md" onClick={goHome} />
              </span>
              {/* Hover label to the right (desktop only) */}
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60] shadow-lg">
                Durga Puja Network
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {['Home', 'About', 'FAQ', 'Search', 'Gallery', 'Calendar', 'Vote', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  if (item === 'Home') {
                    goHome();
                    return;
                  }
                  if (item === 'About') {
                    goToAbout();
                    return;
                  }
                  if (item === 'FAQ') {
                    goToFAQ();
                    return;
                  }
                  if (item === 'Gallery') {
                    goToGallery();
                    return;
                  }
                  if (item === 'Calendar') {
                    goToCalendar();
                    return;
                  }
                  if (item === 'Contact') {
                    goToContact();
                    return;
                  }
                  if (item === 'Search') {
                    // Navigate to home and scroll to search section
                    goHome();
                    setTimeout(() => {
                      scrollToSection('search');
                    }, 300);
                    return;
                  }
                  if (item === 'Vote') {
                    // Navigate to home and scroll to vote section
                    goToVoteSection();
                    return;
                  }
                  scrollToSection(item.toLowerCase());
                }}
                className="text-dpn-olive hover:text-dpn-pink transition-colors duration-200 font-lucy-rose font-medium"
              >
                {item}
              </button>
            ))}
            
            {/* Auth Buttons - Right End */}
            <div className="flex items-center space-x-4 ml-8">
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  {/* User Name with Dropdown */}
                  <button
                    ref={buttonRef}
                    onClick={toggleUserDropdown}
                    className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200 font-lucy-rose font-medium"
                  >
                    <User className="h-5 w-5" />
                    <span className="text-sm">
                      {user.firstName || user.role}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                                     <button
                     onClick={goToLogin}
                     data-login-button
                     className="text-dpn-olive hover:text-dpn-pink transition-colors duration-200 font-lucy-rose font-medium"
                   >
                     Login
                   </button>
                  <button
                    onClick={goToSignup}
                    className="text-dpn-olive hover:text-dpn-pink transition-colors duration-200 font-lucy-rose font-medium"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Popover Menu */}
      {isMenuOpen && (
        <div 
          data-mobile-menu
          className="md:hidden absolute top-full right-4 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-top-2 fade-in duration-200"
        >
          {/* Arrow pointing up to the right */}
          <div className="absolute -top-2 right-6 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
          
          <div className="p-2 space-y-1">
            {['Home', 'About', 'FAQ', 'Search', 'Gallery', 'Calendar', 'Vote', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  if (item === 'Home') {
                    goHome();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'About') {
                    goToAbout();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'FAQ') {
                    goToFAQ();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'Gallery') {
                    goToGallery();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'Calendar') {
                    goToCalendar();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'Contact') {
                    goToContact();
                    setIsMenuOpen(false);
                    return;
                  }
                  if (item === 'Search') {
                    // Navigate to home and scroll to search section
                    goHome();
                    setIsMenuOpen(false);
                    setTimeout(() => {
                      scrollToSection('search');
                    }, 300);
                    return;
                  }
                  if (item === 'Vote') {
                    // Navigate to home and scroll to vote section
                    goToVoteSection();
                    setIsMenuOpen(false);
                    return;
                  }
                  scrollToSection(item.toLowerCase());
                }}
                className="block w-full text-left px-4 py-3 text-dpn-olive hover:text-dpn-pink hover:bg-gray-50 rounded-md transition-colors duration-200 font-lucy-rose font-medium"
              >
                {item}
              </button>
            ))}
            
            {/* Mobile Auth Buttons */}
            {user ? (
              <div className="pt-2 pb-1 border-t border-gray-200 space-y-1">
                <div className="px-4 py-2 text-sm text-dpn-olive bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span className="font-lucy-rose">Logged in as: {user.firstName || user.role}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    getDashboardNavigation()();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-dpn-pink hover:text-dpn-raspberry hover:bg-gray-50 rounded-md transition-all duration-200 font-lucy-rose font-medium"
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4" />
                    <span>{getDashboardButtonText()}</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-dpn-olive hover:text-dpn-pink hover:bg-gray-50 rounded-md transition-all duration-200 font-lucy-rose font-medium"
                >
                  <div className="flex items-center space-x-2">
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </div>
                </button>
              </div>
            ) : (
              <div className="pt-2 pb-1 border-t border-gray-200 space-y-1">
                <button
                  onClick={() => {
                    goToLogin();
                    setIsMenuOpen(false);
                  }}
                  data-login-button
                  className="block w-full text-left px-4 py-3 text-dpn-olive hover:text-dpn-pink hover:bg-gray-50 rounded-md transition-all duration-200 font-lucy-rose font-medium"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    goToSignup();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 bg-dpn-pink hover:bg-dpn-raspberry text-white rounded-md transition-all duration-200 font-lucy-rose font-medium"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>

    {/* Portal Dropdown - Rendered to body */}
    {isUserDropdownOpen && createPortal(
      <div
        ref={dropdownRef}
        className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-[9999]"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }}
      >
        <button
          onClick={() => {
            getDashboardNavigation()();
            setIsUserDropdownOpen(false);
          }}
          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-dpn-pink hover:text-dpn-raspberry hover:bg-gray-50 transition-colors duration-200 font-lucy-rose"
        >
          <Shield className="h-4 w-4" />
          <span>{getDashboardButtonText()}</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-dpn-olive hover:text-dpn-pink hover:bg-gray-50 transition-colors duration-200 font-lucy-rose"
        >
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </button>
      </div>,
      document.body
    )}
    </>
  );
};

export default Header;